import { USER_ACCESS_LEVEL, DQ_PRO_PLAN_KEY, PRODUCT_KEY } from '../../utils/constants';
import { isProductEnabled } from '../../utils/productAccess';
import { isClientWorkspace } from '../../utils/capabilities';
import { resolveWorkspace, PBDQ_WORKSPACE } from '../../config/workspaceNav/solutions';
import { PARTNER_WORKSPACE_NAV } from '../../config/workspaceNav/partnerWorkspace.nav';

export const tutorialEngineConfig = {
    autoNavigate: true, // If true, forces history.push() to the step's route. If false, waits for user to go there manually.
    showSkipButton: true,
    overlayColor: 'rgba(0, 0, 0, 0.3)',
    showDoNotShowAgainCheckbox: true,
    defaultDoNotShowAgainValue: false,
    showTitle: false,
    showDescription: true,
    showStepText: false, // e.g., "1 / 5"
    showNextButton: true,
    showPrevButton: false,
    showCloseIcon: true,
    accentColor: '#FF6900',
};

/**
 * Returns the list of tutorial steps filtered by the user's access level.
 * 
 * @param {object} authUser - The authenticated user object
 * @returns {Array} - Array of step objects
 */
export const getTutorialSteps = (authUser) => {
    const accessLevel = authUser?.access_level;
    const hasLearningPrograms = isProductEnabled(authUser, PRODUCT_KEY.LEARNING_PROGRAM);
    const hasAssessments = isProductEnabled(authUser, PRODUCT_KEY.ASSESSMENT)
        || isProductEnabled(authUser, PRODUCT_KEY.CPC_INDEX_ASSESSMENT);
    // The sidebar's own test: a client workspace is a network with the flag and
    // a school without it; outside one, level 20 and 21 get the Partner sidebar.
    const workspace = isClientWorkspace(authUser)
        ? (authUser?.is_school_network ? PBDQ_WORKSPACE.NETWORK : PBDQ_WORKSPACE.SCHOOL)
        : (resolveWorkspace(authUser) === PBDQ_WORKSPACE.PARTNER ? PBDQ_WORKSPACE.PARTNER : null);
    const allSteps = [
        {
            id: 'step-1-home',
            target: '#sidebar-home-link',
            route: '/my_workspace',
            titleKey: 'tutorial.step1.title',
            contentKey: 'tutorial.step1.content',
            placement: 'right',
            accessLevels: [
                USER_ACCESS_LEVEL.FULL_ACCESS_DQ_INSIDER_PORTAL,
                USER_ACCESS_LEVEL.FULL_ACCESS_DQ_INSIDER_PORTAL_SINGLE_INSTITUTE,
                USER_ACCESS_LEVEL.PARTIAL_ACCESS_DQ_INSIDER_PORTAL_SINGLE_INSTITUTE
            ]
        },
        {
            id: 'step-2-learning-programs',
            target: '#sidebar-products-link',
            route: '/dq_pro/learning/program/list',
            titleKey: 'tutorial.step.learningPrograms.title',
            contentKey: 'tutorial.step.learningPrograms.content',
            placement: 'right',
            accessLevels: [
                USER_ACCESS_LEVEL.FULL_ACCESS_DQ_INSIDER_PORTAL,
                USER_ACCESS_LEVEL.FULL_ACCESS_DQ_INSIDER_PORTAL_SINGLE_INSTITUTE,
                USER_ACCESS_LEVEL.PARTIAL_ACCESS_DQ_INSIDER_PORTAL_SINGLE_INSTITUTE
            ]
        },
        // Partner and School Network only (Ian-PW-19Oct-2, Ian-SNW-19Oct-3).
        {
            id: 'step-my-schools',
            target: '#sidebar-my-schools-link',
            route: '/dq_pro/clients/list',
            titleKey: 'tutorial.step.mySchools.title',
            contentKey: 'tutorial.step.mySchools.content.partner',
            placement: 'right',
            accessLevels: [
                USER_ACCESS_LEVEL.FULL_ACCESS_DQ_INSIDER_PORTAL,
                USER_ACCESS_LEVEL.FULL_ACCESS_DQ_INSIDER_PORTAL_SINGLE_INSTITUTE
            ]
        },
        // Partner only (Ian-PW-19Oct-2).
        {
            id: 'step-opportunity-pipeline',
            target: '#sidebar-opportunity-pipeline-link',
            route: '/dq_pro/growth-opportunities/pipeline',
            titleKey: 'tutorial.step.pipeline.title',
            contentKey: 'tutorial.step.pipeline.content',
            placement: 'right',
            accessLevels: [
                USER_ACCESS_LEVEL.FULL_ACCESS_DQ_INSIDER_PORTAL,
                USER_ACCESS_LEVEL.FULL_ACCESS_DQ_INSIDER_PORTAL_SINGLE_INSTITUTE
            ]
        },
        /*
         * REMOVED: step-3-assessments, target '#sidebar-assessment-list-link'.
         *
         * That sidebar item no longer exists. The PbDQ Navigation Bar sheet
         * removes "Assessments" from the menu (row 13 — the preview moves to
         * Solutions > COS Children Assessment > Preview), so nothing renders
         * that id in any sidebar any more.
         *
         * A step whose target never appears is not a harmless dead step:
         * TutorialManager force-navigates to the step's `route` on every path
         * change, so the tutorial parks the user on /dq_pro/assessment/list and
         * undoes every attempt to navigate away.
         *
         * If assessments need a tutorial step again, point it at an element that
         * exists and give it a route the sidebar can actually reach.
         */
        {
            id: 'step-4-classes',
            target: '#sidebar-classes-link',
            route: '/dq_pro/classes',
            titleKey: 'tutorial.step.classes.title',
            contentKey: 'tutorial.step.classes.content',
            placement: 'right',
            accessLevels: [
                USER_ACCESS_LEVEL.FULL_ACCESS_DQ_INSIDER_PORTAL_SINGLE_INSTITUTE,
                // School workspace only — see hasClassesAccess below (Ian-SW-19Oct-1).
                USER_ACCESS_LEVEL.PARTIAL_ACCESS_DQ_INSIDER_PORTAL_SINGLE_INSTITUTE
            ]
        },
        {
            id: 'step-5-profile-menu',
            target: '#user-profile-menu-button',
            route: '/my_workspace',
            titleKey: 'tutorial.step.profile.title',
            contentKey: 'tutorial.step.profile.content',
            placement: 'bottom-end',
            accessLevels: [
                USER_ACCESS_LEVEL.FULL_ACCESS_DQ_INSIDER_PORTAL,
                USER_ACCESS_LEVEL.FULL_ACCESS_DQ_INSIDER_PORTAL_SINGLE_INSTITUTE,
                USER_ACCESS_LEVEL.PARTIAL_ACCESS_DQ_INSIDER_PORTAL_SINGLE_INSTITUTE
            ]
        }
    ];

    // Return only the steps that this user's access level permits
    let validSteps = allSteps.filter(step => step.accessLevels.includes(accessLevel));

    // Filter out step-2 if the user doesn't have learning programs
    if (!hasLearningPrograms) {
        validSteps = validSteps.filter(step => step.id !== 'step-2-learning-programs');
    }

    // Filter out step-3 if the user doesn't have assessments
    if (!hasAssessments) {
        validSteps = validSteps.filter(step => step.id !== 'step-3-assessments');
    }

    // Filter out step-4 if the user doesn't have a paid plan for classes
    const hasClassesAccess = authUser?.access_level === USER_ACCESS_LEVEL.FULL_ACCESS_DQ_INSIDER_PORTAL_SINGLE_INSTITUTE &&
        authUser?.dq_pro_plan?.status === 'active' &&
        authUser?.dq_pro_plan?.plan_key !== DQ_PRO_PLAN_KEY.DEFAULT;

    // A school workspace always shows My Classes in its sidebar (SW_MC).
    if (!hasClassesAccess && workspace !== PBDQ_WORKSPACE.SCHOOL) {
        validSteps = validSteps.filter(step => step.id !== 'step-4-classes');
    }

    /*
     * Partner, School Network and School workspaces: their own text and step
     * order (Ian-PW-19Oct-2, Ian-SNW-19Oct-3, Ian-SW-19Oct-1). Every other workspace keeps the steps
     * above unchanged. A step whose sidebar row is hidden is left out — the
     * tutorial would otherwise wait on that page for a target that never shows.
     */
    const showsMySchools = workspace === PBDQ_WORKSPACE.NETWORK
        || (workspace === PBDQ_WORKSPACE.PARTNER
            && PARTNER_WORKSPACE_NAV.find((row) => row.code === 'PW_MS').visibleWhen({ authUser }));
    if (!showsMySchools) {
        validSteps = validSteps.filter(step => step.id !== 'step-my-schools');
    }
    if (workspace !== PBDQ_WORKSPACE.PARTNER) {
        validSteps = validSteps.filter(step => step.id !== 'step-opportunity-pipeline');
    }
    const WORKSPACE_STEPS = {
        [PBDQ_WORKSPACE.PARTNER]: {
            order: ['step-1-home', 'step-my-schools', 'step-opportunity-pipeline', 'step-2-learning-programs', 'step-5-profile-menu'],
            content: {
                'step-1-home': 'tutorial.step1.content.partner',
            },
        },
        [PBDQ_WORKSPACE.NETWORK]: {
            order: ['step-1-home', 'step-my-schools', 'step-4-classes', 'step-2-learning-programs', 'step-5-profile-menu'],
            content: {
                'step-1-home': 'tutorial.step1.content.network',
                'step-my-schools': 'tutorial.step.mySchools.content.network',
                'step-4-classes': 'tutorial.step.classes.content.network',
                'step-2-learning-programs': 'tutorial.step.learningPrograms.content.network',
            },
        },
        [PBDQ_WORKSPACE.SCHOOL]: {
            order: ['step-1-home', 'step-4-classes', 'step-2-learning-programs', 'step-5-profile-menu'],
            content: {
                'step-1-home': 'tutorial.step1.content.school',
                'step-4-classes': 'tutorial.step.classes.content.school',
                'step-2-learning-programs': 'tutorial.step.learningPrograms.content.school',
            },
        },
    };
    const own = WORKSPACE_STEPS[workspace];
    if (own) {
        validSteps = validSteps
            .map(step => (own.content[step.id] ? { ...step, contentKey: own.content[step.id] } : step))
            .sort((a, b) => own.order.indexOf(a.id) - own.order.indexOf(b.id));
    }

    return validSteps;
};

// ─────────────────────────────────────────────────────────────────────────────
// Situational / Contextual Tutorial Definitions
// These are NOT part of the global onboarding flow — they are triggered from
// specific pages when data conditions are met (one-time, per-user localStorage guard).
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Tutorial A — "Program Complete → Link to Classes"
 * Triggered on /my_workspace when a Level-21 user has exactly one completed program.
 * Highlights the "Your Classes" left-nav item with a dark-themed popover matching
 * the design shown in the screenshot.
 *
 * localStorage dismiss key: `tutorial_classes_nudge_${userId}`
 */
export const TUTORIAL_CLASSES_NUDGE_STEPS = [
    {
        id:          'classes-nudge',
        target:      '#sidebar-classes-link',
        route:       '/my_workspace',
        contentKey:  'tutorial.classesNudge.content',
        placement:   'right',
        darkTheme:   true,       // Dark card matching screenshot
        richContent: true,       // Multi-paragraph bold headline rendering
        showTitle:   false,
        showPrevButton: false,
        showCloseIcon:  true,
        showDoNotShowAgainCheckbox: false,
        accessLevels: [
            USER_ACCESS_LEVEL.FULL_ACCESS_DQ_INSIDER_PORTAL_SINGLE_INSTITUTE,
        ],
    },
];

